import type React from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@/styled-system/css';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import numberUtils from 'app/shared/utils/numberUtils';
import IconArrowDown from 'images/icons/compare-arrow-down.svg';
import IconArrowUp from 'images/icons/compare-arrow-up.svg';
import Card from '../../../ui/Card';
import {
  areaRow,
  areaRowLink,
  areaRowValue,
  averagesCardHeader,
  averagesList,
  colorCheaper,
  colorMore,
  container,
  deltaIcon,
  description,
  inlineDelta,
  inlineLink,
  summaryDelta,
  summaryRow,
} from './pricingComparisonModule.styles';

type AreaStatShape = {
  id: string;
  name: string;
  type: string;
  link: string;
  listPrice: number;
  areaAveragePrice: number;
  averagePrice: number;
  difference: string;
  percentageValue: number;
};

type SurroundingAreaStats = {
  neighborhood: AreaStatShape | null;
  city: AreaStatShape | null;
  county: AreaStatShape | null;
  zip: AreaStatShape | null;
};

type Floorplan = {
  beds?: number;
};

type PricingListing = {
  active?: boolean;
  isAllowedToRetain?: boolean;
  isApartmentBldg?: boolean;
  showPricingStats?: boolean;
  listingType?: string;
  listingMinMaxPriceBeds?: { minPrice?: number };
  floorplans?: Array<Floorplan>;
  areas?: { surroundingAreaStats?: SurroundingAreaStats };
};

type PreviewKind = 'less' | 'more' | 'equal';

type PreviewData = {
  kind: PreviewKind;
  areaKey: 'neighborhood' | 'city' | 'county';
  stat: AreaStatShape;
};

const getPreview = (stats: SurroundingAreaStats | undefined): PreviewData | null => {
  if (!stats) return null;
  const stat = stats.neighborhood ?? stats.city ?? stats.county;
  if (!stat) return null;

  let areaKey: PreviewData['areaKey'] = 'county';
  if (stats.neighborhood) areaKey = 'neighborhood';
  else if (stats.city) areaKey = 'city';

  let kind: PreviewKind = 'equal';
  if (stat.percentageValue < 0) kind = 'less';
  else if (stat.percentageValue > 0) kind = 'more';

  return { kind, areaKey, stat };
};

const getHomeQualifier = (listingType: string | undefined, beds: number | undefined): [string, string] => {
  if (listingType === 'room') return ['room for rent', 'rooms for rent'];
  if (beds === 0) return ['studio', 'studios'];
  if (typeof beds === 'number') return [`${beds} bedroom home`, `${beds} bedroom homes`];
  return ['home', 'homes'];
};

const colorClassForKind = (kind: PreviewKind) => {
  if (kind === 'less') return colorCheaper;
  if (kind === 'more') return colorMore;
  return undefined;
};

const descriptionPhraseForKind = (kind: PreviewKind) => {
  if (kind === 'less') return 'cheaper than';
  if (kind === 'more') return 'more than';
  return 'similarly to';
};

/**
 * PricingComparisonModule
 *
 * Shows how the current listing's price compares to the market average in its
 * neighborhood, city, and county. Ported from the legacy SurroundingStats HDP
 * module. Content is inline only — the ModuleRenderer wraps this in a Module
 * with the title "Pricing comparison".
 */
export const PricingComparisonModule: React.FC = () => {
  const listing = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing as PricingListing | undefined,
  );

  const surroundingAreaStats = listing?.areas?.surroundingAreaStats;
  const preview = getPreview(surroundingAreaStats);
  if (!preview) return null;

  const minPrice = listing?.listingMinMaxPriceBeds?.minPrice ?? 0;
  const listingPriceDisplay = `$${numberUtils.prettyPrintNumber(minPrice)}`;
  const beds = listing?.floorplans?.[0]?.beds;
  const [singular, plural] = getHomeQualifier(listing?.listingType, beds);
  const colorClass = colorClassForKind(preview.kind);
  const phrase = descriptionPhraseForKind(preview.kind);
  const areaName = preview.stat.name;
  const areaHref = preview.stat.link;

  const areaEntries: Array<AreaStatShape> = [
    surroundingAreaStats?.neighborhood,
    surroundingAreaStats?.city,
    surroundingAreaStats?.county,
    surroundingAreaStats?.zip,
  ].filter((stat): stat is AreaStatShape => stat != null);

  return (
    <div className={container}>
      {preview.kind !== 'equal' && (
        <div className={summaryRow}>
          <img src={preview.kind === 'less' ? IconArrowDown : IconArrowUp} className={deltaIcon} height="20" alt="" />
          <span className={cx(summaryDelta, colorClass)}>{preview.stat.difference}</span>
        </div>
      )}

      <p className={description}>
        At {listingPriceDisplay}, this listing is priced{' '}
        {preview.kind !== 'equal' && (
          <>
            <strong className={cx(inlineDelta, colorClass)}>{preview.stat.difference}</strong>{' '}
          </>
        )}
        {phrase} the current market rate for a {singular} in{' '}
        <a className={inlineLink} href={areaHref}>
          {areaName}
        </a>
        .
      </p>

      {areaEntries.length > 0 && (
        <Card variant="default" size="md">
          <h3 id="pricing-comparison-averages-header" className={averagesCardHeader}>
            Other {plural} in the area go for
          </h3>
          <ul className={averagesList} aria-labelledby="pricing-comparison-averages-header">
            {areaEntries.map((stat) => (
              <li key={stat.id} className={areaRow}>
                <a href={stat.link} className={areaRowLink}>
                  {stat.name}
                </a>
                <span className={areaRowValue}>${numberUtils.prettyPrintNumber(stat.areaAveragePrice)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};
